import { useUserStore } from '@qse/stores'
import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'

import { setSentryContext, setSentryTags, setSentryUser } from '../context'

/**
 * Hook to automatically sync user context with Sentry
 */
export function useSentryUser(): void {
  const user = useUserStore(state => state.user)
  const location = useLocation()

  useEffect(() => {
    // Set user context
    if (user?.id) {
      setSentryUser(user.id, user.tenant?.id)
    } else {
      setSentryUser(null)
    }
  }, [user?.id, user?.tenant?.id])

  useEffect(() => {
    // Set route context
    setSentryTags({
      route: location.pathname,
    })

    // Set browser and screen context
    if (typeof window !== 'undefined') {
      setSentryContext('browser', {
        name: getBrowserName(),
        version: getBrowserVersion(),
        os: getOSName(),
        screen: {
          width: window.screen.width,
          height: window.screen.height,
        },
        viewport: {
          width: window.innerWidth,
          height: window.innerHeight,
        },
      })
    }
  }, [location.pathname])
}

/**
 * Get browser name from user agent
 */
function getBrowserName(): string {
  const ua = navigator.userAgent

  if (ua.includes('Chrome') && !ua.includes('Edg')) return 'Chrome'
  if (ua.includes('Firefox')) return 'Firefox'
  if (ua.includes('Safari') && !ua.includes('Chrome')) return 'Safari'
  if (ua.includes('Edg')) return 'Edge'
  if (ua.includes('Opera') || ua.includes('OPR')) return 'Opera'

  return 'Unknown'
}

/**
 * Get browser version from user agent
 */
function getBrowserVersion(): string {
  const ua = navigator.userAgent
  const match = ua.match(/(Chrome|Firefox|Safari|Edg|Opera|OPR)\/(\d+)/)

  return match ? match[2] : 'Unknown'
}

/**
 * Get OS name from user agent
 */
function getOSName(): string {
  const ua = navigator.userAgent

  if (ua.includes('Windows')) return 'Windows'
  if (ua.includes('Mac OS X') || ua.includes('Macintosh')) return 'macOS'
  if (ua.includes('Linux')) return 'Linux'
  if (ua.includes('Android')) return 'Android'
  if (ua.includes('iOS') || ua.includes('iPhone') || ua.includes('iPad')) return 'iOS'

  return 'Unknown'
}
